import {
  createRouter,
  ErrorComponent,
  type ErrorComponentProps,
  RouterProvider,
  type ToOptions,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { setSentryRouter } from "@blis/telemetry/client";

import { LoadingPage } from "@/components/LoadingPage";
import { StaleVersionError } from "@/components/StaleVersionError";
import { AppProvider } from "@/context";
import { isElectron } from "@/lib/electron";
import { isChunkLoadError, isStaleDeployment } from "@/utils/version";
import { routeTree } from "../routeTree.gen";

function DefaultErrorComponent({ error }: ErrorComponentProps) {
  const [stale, setStale] = useState<boolean | null>(null);

  useEffect(() => {
    if (isChunkLoadError(error)) {
      setStale(null);
      isStaleDeployment().then(setStale);
    }
  }, [error]);

  if (isChunkLoadError(error) && stale) {
    return <StaleVersionError />;
  }

  return <ErrorComponent error={error} />;
}

const router = createRouter({
  routeTree,
  // routeMasks: [accessionMask, boardMask],
  defaultPreload: isElectron() ? "render" : "intent",
  defaultPendingComponent: LoadingPage,
  defaultErrorComponent: DefaultErrorComponent,
});

setSentryRouter(router);

declare function cwr(operation: string, payload: unknown): void;

router.subscribe("onResolved", (event) => {
  if (typeof cwr !== "undefined") {
    cwr("recordPageView", event.toLocation.pathname);
  }
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  interface HistoryState {
    label?: string;
    href?: ToOptions;
    selected?: string | number | null;
    open?: Record<string, string>;
    preserveDefinition?: unknown;
  }
}

function App() {
  return (
    <AppProvider>
      <RouterProvider router={router} />
    </AppProvider>
  );
}

export default App;
